import React, { createContext, useContext, useState, useEffect } from 'react';

const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return `${parseInt(h.substring(0, 2), 16)} ${parseInt(h.substring(2, 4), 16)} ${parseInt(h.substring(4, 6), 16)}`;
};

const themes = {
  dark: {
    name: 'dark',
    label: 'Dark Mode',
    colors: {
      bg: '#050505',
      bgSecondary: '#121212',
      bgCard: '#0C0C0C',
      text: '#EDEDED',
      textSecondary: '#A1A1A1',
      textMuted: '#6B6B6B',
      primary: '#fdcd79',
      primaryHover: '#e6b464',
      secondary: '#fdcd79',
      accent: '#fdcd79',
      border: '#2A2A2A',
      borderHover: '#3A3A3A',
      success: '#22C55E',
      error: '#fdcd79',
      warning: '#B46432',
    }
  },
  light: {
    name: 'light',
    label: 'Light Mode',
    colors: {
      bg: '#FFFFFF',
      bgSecondary: '#F8F8F8',
      bgCard: '#FFFFFF',
      text: '#2C2C2C',
      textSecondary: '#555555',
      textMuted: '#777777',
      primary: '#59090c',
      primaryHover: '#46070a',
      secondary: '#59090c',
      accent: '#59090c',
      border: '#E5E7EB',
      borderHover: '#D1D5DB',
      success: '#22A050',
      error: '#59090c',
      warning: '#B46432',
    }
  }
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('light');

  useEffect(() => {
    const saved = localStorage.getItem('sentora-theme');
    if (saved && themes[saved]) setCurrentTheme(saved);
  }, []);

  useEffect(() => {
    const theme = themes[currentTheme] || themes.dark;
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, hexToRgb(value));
    });
    root.setAttribute('data-theme', currentTheme);
    if (currentTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('sentora-theme', currentTheme);
  }, [currentTheme]);

  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const setTheme = (name) => {
    if (themes[name]) {
      setCurrentTheme(name);
      localStorage.setItem('sentora-theme', name);
    }
  };

  const getTheme = () => themes[currentTheme] || themes.dark;
  const getAllThemes = () => themes;

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, toggleTheme, getTheme, getAllThemes, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

export { themes };
export default ThemeContext;
